/**
 * Sticky action bar for ReviewQuestionnaire — extracted so the page
 * stays under the 500-line cap. Renders the questionnaire status,
 * an optional risk score, and the Approve / Reject / Request Info
 * buttons. The Approve button respects the CR-5362 rollup gate.
 */
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { CheckCircle2, XCircle, MessageSquare } from 'lucide-react';
import { statusColors } from './reviewQuestionnaireUtils';
import type { RiskAssessment } from '../../lib/api';

interface ReviewActionBarProps {
  filledDatasetStatus: string;
  riskAssessment: RiskAssessment | null;
  canFinalApprove: boolean;
  finalApproveTooltip?: string;
  onApproveClick: () => void;
  onRejectClick: () => void;
  onMoreInfoClick: () => void;
}

export default function ReviewActionBar({
  filledDatasetStatus,
  riskAssessment,
  canFinalApprove,
  finalApproveTooltip,
  onApproveClick,
  onRejectClick,
  onMoreInfoClick,
}: ReviewActionBarProps) {
  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-t shadow-lg z-50">
        <div className="container max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Badge className={statusColors[filledDatasetStatus] || 'bg-gray-100'}>
                {filledDatasetStatus.replace('_', ' ')}
              </Badge>
              {riskAssessment && (
                <Badge variant="outline" className={`${
                  riskAssessment.risk_level === 'low' ? 'text-green-600 border-green-300' :
                  riskAssessment.risk_level === 'medium' ? 'text-yellow-600 border-yellow-300' :
                  riskAssessment.risk_level === 'high' ? 'text-orange-600 border-orange-300' :
                  'text-red-600 border-red-300'
                }`}>
                  {riskAssessment.score.toFixed(0)}% Score - {riskAssessment.risk_display.label}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={onMoreInfoClick}>
                <MessageSquare className="h-4 w-4 mr-2" />
                Request Info
              </Button>
              <Button
                variant="outline"
                onClick={onRejectClick}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </Button>
              <Button
                onClick={onApproveClick}
                className="bg-green-600 hover:bg-green-700"
                disabled={!canFinalApprove}
                title={finalApproveTooltip}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Approve
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Spacer for the sticky bar */}
      <div className="h-20" />
    </>
  );
}
